import { useEffect, useRef, useState } from 'react';
import { Button, ErrorState } from '@/core/ui';
import { ArrowCounterClockwise, CheckCircle, Sparkle, WarningCircle, X } from '@/core/ui/icons';
import { usePreviewFlow } from '../../application/queries';
import type { Answer, BotMessage, Ending, FlowDoc, Prompt, Step } from '../../domain/types';
import { Composer } from './Composer';
import { Avatar, BotBubble, Typing, UserBubble } from './Messages';

type Shown = Exclude<BotMessage, { type: 'pause' }>;
type Item = { kind: 'bot'; message: Shown } | { kind: 'user'; text: string };

interface Props {
  doc: FlowDoc;
  flowName: string;
  startGroupId?: string;
  onRestart: (startGroupId?: string) => void;
  onClose: () => void;
  /** Pausa de "digitando" entre mensagens. 0 nos testes. */
  typingMs?: number;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * "Testar": roda o rascunho atual (mesmo sem salvar) pela API de preview,
 * como o portal mostraria — nada é gravado, webhooks e visitas não saem.
 * O `state` devolvido a cada passo volta junto da próxima resposta.
 */
export function TestPanel({ doc, flowName, startGroupId, onRestart, onClose, typingMs }: Props) {
  const preview = usePreviewFlow();
  const [items, setItems] = useState<Item[]>([]);
  const [queue, setQueue] = useState<BotMessage[]>([]);
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [ending, setEnding] = useState<Ending | null>(null);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [state, setState] = useState<Record<string, unknown> | null>(null);
  const [turn, setTurn] = useState(0);
  const [delay] = useState(() => typingMs ?? (prefersReducedMotion() ? 0 : 650));
  const seq = useRef(0);
  const scroller = useRef<HTMLDivElement>(null);

  const apply = (step: Step) => {
    if (step.answer) setItems((list) => [...list, { kind: 'user', text: step.answer! }]);
    setInvalid(step.invalid);
    setQueue((q) => [...q, ...step.messages]);
    setPrompt(step.prompt);
    setEnding(step.ending);
    // Resposta recusada: a mesma pergunta, com o que foi digitado ainda no campo.
    if (!step.invalid) setTurn((t) => t + 1);
  };

  const send = (answer: Answer | null, current: Record<string, unknown> | null) => {
    const id = ++seq.current;
    preview.mutate(
      {
        definition: doc,
        flow_name: flowName || 'Teste',
        state: current,
        answer,
        start_group_id: startGroupId ?? null,
      },
      {
        onSuccess: (out) => {
          // Só a resposta do pedido mais recente vale.
          if (id !== seq.current) return;
          setState(out.state);
          apply(out.step);
        },
      },
    );
  };

  // Começa assim que abre (o painel remonta a cada "Recomeçar").
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    send(null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só ao abrir
  }, []);

  // As mensagens aparecem uma a uma, com o "digitando" entre elas; a pausa do bloco Esperar dura o que pediu.
  useEffect(() => {
    const head = queue[0];
    if (!head) return;
    const wait = head.type === 'pause' ? (delay ? Math.min(head.seconds, 10) * 1000 : 0) : delay;
    const t = window.setTimeout(() => {
      setQueue((q) => q.slice(1));
      if (head.type !== 'pause') setItems((list) => [...list, { kind: 'bot', message: head }]);
    }, wait);
    return () => window.clearTimeout(t);
  }, [queue, delay]);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [items, queue.length, prompt, ending]);

  const typing = queue.length > 0 || (preview.isPending && !prompt);
  const settled = queue.length === 0;

  return (
    <section aria-label="Teste do fluxo" className="flex h-full flex-col">
      <header className="flex items-center gap-2 px-4 pt-4 pb-3">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold text-fg">Testar {flowName}</h2>
          <p className="text-xs text-fg-subtle">
            Rascunho atual, sem salvar nada. Webhooks e visitas não saem.
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => onRestart(startGroupId)}>
          <ArrowCounterClockwise aria-hidden className="size-4" />
          Recomeçar
        </Button>
        <button
          type="button"
          aria-label="Fechar teste"
          onClick={onClose}
          className="rounded-lg p-1.5 text-fg-subtle hover:bg-surface-2 hover:text-fg"
        >
          <X aria-hidden className="size-4" />
        </button>
      </header>

      <div
        ref={scroller}
        aria-live="polite"
        className="min-h-0 flex-1 space-y-2.5 overflow-y-auto bg-canvas/60 px-4 py-4"
      >
        {items.map((item, i) =>
          item.kind === 'user' ? (
            <UserBubble key={i} text={item.text} />
          ) : item.message.type === 'notice' ? (
            <BotBubble key={i} message={item.message} />
          ) : (
            <div key={i} className="flex items-end gap-2">
              <Avatar />
              <BotBubble message={item.message} />
            </div>
          ),
        )}
        {typing && (
          <div className="flex items-end gap-2">
            <Avatar />
            <Typing />
          </div>
        )}
        {settled && ending && <EndingNote ending={ending} />}
        {preview.isError && (
          <ErrorState error={preview.error} onRetry={() => onRestart(startGroupId)} className="bg-surface" />
        )}
      </div>

      <footer className="px-4 pt-3 pb-4">
        {settled && prompt && !ending ? (
          <Composer
            key={`${prompt.block_id}-${turn}`}
            prompt={prompt}
            busy={preview.isPending}
            invalid={invalid}
            onAnswer={(a) => send(a, state)}
          />
        ) : settled && ending ? (
          <Button variant="secondary" className="w-full" onClick={() => onRestart(startGroupId)}>
            <ArrowCounterClockwise aria-hidden className="size-4" />
            Testar de novo
          </Button>
        ) : (
          <p className="text-center text-xs text-fg-subtle">{typing ? 'Aguarde…' : ' '}</p>
        )}
      </footer>
    </section>
  );
}

function EndingNote({ ending }: { ending: Ending }) {
  if (ending.kind === 'handoff')
    return (
      <div className="space-y-2 rounded-2xl bg-nav-active p-3.5 text-sm">
        <p className="flex items-center gap-2 font-medium text-nav-active-fg">
          <Sparkle aria-hidden className="size-4" />
          Passaria para a IA
        </p>
        {ending.question && (
          <p className="text-fg">
            <span className="block text-xs text-fg-subtle">Pergunta enviada</span>
            {ending.question}
          </p>
        )}
        {ending.summary && (
          <p className="whitespace-pre-wrap text-fg-muted">
            <span className="block text-xs text-fg-subtle">Resumo para a IA</span>
            {ending.summary}
          </p>
        )}
      </div>
    );
  const failed = ending.kind === 'failed';
  const Icon = failed ? WarningCircle : CheckCircle;
  return (
    <p className="flex items-center justify-center gap-1.5 py-2 text-xs text-fg-subtle">
      <Icon aria-hidden className={failed ? 'size-4 text-rose-500' : 'size-4 text-brand-500'} />
      {failed ? 'O fluxo terminou com erro' : 'Fim do fluxo'}
    </p>
  );
}
